// Pure model for the Bad Bible space: turns one sheet tab's raw rows into
// sections of readable entries, and finds Bible references worth linking.
//
// The rows arrive exactly as the source Google Sheet holds them (see
// scripts/seedBadBible.js), so the sheet's layout conventions are read here
// rather than normalised at seed time: a row with one filled cell is a section
// title, a short row right after it (or first in the tab) is a column header,
// and a blank first cell means "same group as the row above".

// Tabs whose grids read better as the sheet than as entries.
export const TABLE_DEFAULT = new Set([
	'NT MSS',
	'Passover Timline',
	'Empty Tomb 1-Trip',
	'Empty Tomb 2-Trips',
	'Appendix',
	'KJV',
	'MSRI Calc',
]);

// Only these tabs quote the Bible in their reference cells. The rest cite the
// Quran, the Talmud, Herodotus and so on, where a BibleGateway link would be wrong.
export const BIBLE_TABS = new Set([
	'Worst Bits',
	'Contradictions',
	'Apocrypha',
	'Scientific Errors',
	'Anachronisms',
	'JC Prophecies',
	'JC Lies',
	'Failed Prophecies',
	'Omni',
	'Trinity',
	'FLERF',
	'Love',
	'Conduct',
	'FreeWill',
	'Heaven',
	'LAWS',
	'Philbro',
]);

const REF = /^(?:[1-3]\s?|I{1,3}\s)?[A-Z][a-z]+\.?\s?\d+[.:]\d+/;

export const slug = (s) =>
	s
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '');

const filled = (r) => r.filter((c) => c).length;
const headerish = (r) =>
	filled(r) >= 2 && r.every((c) => !c || (c.length <= 60 && !c.includes('\n')));

export function parseTab(rows) {
	const ncol = rows.length ? rows[0].length : 0;
	const sections = [];
	let sec = null;
	let header = null;
	let group = '';
	const open = (title) => {
		sec = { title, entries: [] };
		sections.push(sec);
	};

	for (let i = 0; i < rows.length; i++) {
		const r = rows[i];
		const n = filled(r);
		const isTitle =
			n === 1 &&
			(ncol > 1
				? Boolean(r[0]) && r[0].length <= 140
				: i === 0 || (r[0].length <= 70 && !r[0].includes('\n')));
		if (isTitle) {
			open(r[0]);
			group = '';
			if (rows[i + 1] && headerish(rows[i + 1])) {
				header = rows[i + 1];
				i++;
			}
			continue;
		}
		if (i === 0 && headerish(r)) {
			header = r;
			continue;
		}
		if (!sec) open('');
		const labels = header || [];
		const cells = r
			.map((value, j) => ({
				label: labels[j] && !/^\d+$/.test(labels[j]) ? labels[j] : '',
				value,
			}))
			.filter((c) => c.value);
		const eyebrow = ncol > 1 && !r[0] && group ? group : '';
		if (r[0]) group = r[0].split('\n')[0];
		sec.entries.push({
			cells,
			eyebrow,
			text: `${r.join(' • ')} ${eyebrow}`.toLowerCase(),
		});
	}
	return sections.filter((s) => s.entries.length || s.title);
}

// The BibleGateway search string for a cell, or null. Takes the first line and
// cuts it before any quoted text or aside, so "Sirach 29:10-11 – Lose your
// silver" searches "Sirach 29:10-11".
export function bibleRef(tabName, value) {
	if (!BIBLE_TABS.has(tabName)) return null;
	const first = value.split('\n')[0].trim();
	if (!REF.test(first)) return null;
	const ref = first
		.split(/\s[–—-]\s|\(|“|"/)[0]
		.trim()
		.replace(/[;,.\s]+$/, '');
	if (!ref || ref.length > 220) return null;
	return ref;
}

export const bibleGatewayUrl = (ref) =>
	`https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=NRSVUE`;

// Build the tab list the page renders from the stored docs.
export function buildTabs(docs) {
	return [...docs]
		.sort((a, b) => a.order - b.order)
		.map((d) => {
			const rows = JSON.parse(d.rowsJson || '[]');
			const sections = parseTab(rows);
			return {
				id: d.id,
				name: d.name,
				rows,
				sections,
				count: sections.reduce((a, s) => a + s.entries.length, 0),
			};
		});
}
